import { useEffect, useMemo, useState } from 'react';

import {
  DEFAULT_LOCALE,
  getPreferredLocale,
} from '@/services/locale.service';

import messages from './messages';

function getNodeByPath(source, pathParts = []) {
  let current = source;
  for (let i = 0; i < pathParts.length; i += 1) {
    const key = pathParts[i];
    if (current === null || current === undefined || !(key in current)) {
      return undefined;
    }
    current = current[key];
  }
  return current;
}

function interpolate(template, values) {
  if (!values || typeof values !== 'object') return template;
  return template.replace(/\{([^{}]+)\}/g, (match, key) => {
    if (Object.prototype.hasOwnProperty.call(values, key)) {
      return String(values[key]);
    }
    return match;
  });
}

function resolveMessage(locale, namespaceParts, keyParts) {
  const localeMessages = messages[locale] || messages[DEFAULT_LOCALE];
  const fallbackMessages = messages[DEFAULT_LOCALE] || {};
  const localeNamespace = getNodeByPath(localeMessages, namespaceParts);
  const fallbackNamespace = getNodeByPath(fallbackMessages, namespaceParts);

  const fromLocale = getNodeByPath(localeNamespace, keyParts);
  if (typeof fromLocale === 'string') return fromLocale;

  const fromFallback = getNodeByPath(fallbackNamespace, keyParts);
  if (typeof fromFallback === 'string') return fromFallback;

  return null;
}

const useTranslations = (namespace = '') => {
  const [locale, setLocale] = useState(() => getPreferredLocale());

  useEffect(() => {
    const handleLocaleChange = (event) => {
      const next = event?.detail?.locale || getPreferredLocale();
      setLocale(next);
    };

    window.addEventListener('suarte:preferred-language-changed', handleLocaleChange);
    return () => {
      window.removeEventListener('suarte:preferred-language-changed', handleLocaleChange);
    };
  }, []);

  return useMemo(() => {
    const namespaceParts = namespace.split('.').filter(Boolean);
    return (key, values) => {
      const keyParts = String(key).split('.').filter(Boolean);
      const message = resolveMessage(locale, namespaceParts, keyParts);
      if (message === null) return key;
      return interpolate(message, values);
    };
  }, [locale, namespace]);
};

export default useTranslations;
